import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, signal } from '@angular/core'
import { form } from '@angular/forms/signals'
import { MatSnackBar } from '@angular/material/snack-bar'
import { DateService } from '@lemon/core/service/date.service'
import { firstValueFrom, Observable, of, Subject } from 'rxjs'
import { map, switchMap, take, takeUntil, tap } from 'rxjs/operators'

import { AsiakasService, AsiakkaanAvainTiedot } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjanpitoImageHandlerService, TositeJaNaytettavaSivut } from 'app/_angular/service/kirjanpito/kirjanpito.service'
import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { arvonMuutokset } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { Asiakas } from '../../_jaettu-lemonator/model/asiakas'
import { AlvMaaritys, AsiakkaanTilikartta, KirjaukseenLiitettyjenTiedostojenSivut, Kirjaus, Kirjausrivi, MyyntiAlvt, OstoAlvt, Paatilikartta } from '../../_jaettu-lemonator/model/kirjanpito'
import { KirjanpitajanNimitiedot } from '../../_jaettu-lemonator/model/kirjanpitaja'
import { KirjausehdotusPalauteRivi } from '../../_jaettu-lemonator/model/llm-kirjausehdotus-palaute'
import { AsiakasJaettuService } from '../../_jaettu-lemonator/service/asiakas-jaettu.service'
import { KirjanpitoUriService } from '../../_jaettu-lemonator/service/kirjanpito-uri.service'
import { TilikarttaJaettuService } from '../../_jaettu-lemonator/service/tilikartta-jaettu.service'
import { callLlmKirjausehdotusPalaute } from './llm-kirjausehdotus-palaute.client'

const _ALV_TILINUMEROT = new Set(['1763', '2939', '2946'])

@Component({
  selector: 'app-llm-kirjausehdotus-palaute',
  templateUrl: './llm-kirjausehdotus-palaute.component.html',
  styleUrls: ['../../kirjanpito/kirjanpito.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LlmKirjausehdotusPalauteComponent implements OnInit, OnDestroy {
  readonly palautteet = signal<KirjausehdotusPalauteRivi[]>([])
  readonly lataa = signal(false)
  naytaKasitellyt = false
  naytaKasitellytKentta = form(signal(false))
  readonly merkitaanKasitellyiksi = signal<ReadonlySet<string>>(new Set())

  readonly valittuPalaute = signal<KirjausehdotusPalauteRivi | null>(null)
  readonly naytettavatTositteetJaSivut = signal<TositeJaNaytettavaSivut[] | null>([])

  readonly naytettavaKirjaus = signal<Kirjaus | null>(null)
  readonly kirjausLataa = signal(false)

  readonly tyhjaKirjausObservable: Observable<null> = of(null)

  @ViewChild('tositeContainer', { static: false }) tositeContainerRef: ElementRef<HTMLDivElement>

  private readonly _kirjanpitajatMap = signal<Map<string, KirjanpitajanNimitiedot>>(new Map())
  private _ngUnsubscribe = new Subject<void>()
  private _valittuPalauteSubject = new Subject<KirjausehdotusPalauteRivi | null>()

  private readonly _alvTunnisteenMukaan = new Map<string, AlvMaaritys>()
  private readonly _tiliNimetAsiakkaittain = signal<ReadonlyMap<string, Map<string, string>>>(new Map())
  private _paatilikarttaPromise: Promise<Paatilikartta> | null = null

  constructor(
    private _firebase: FirebaseLemonator,
    private _snackbar: MatSnackBar,
    private _kirjanpitajaService: KirjanpitajaService,
    private _asiakasService: AsiakasService,
    private _kirjanpitoUriService: KirjanpitoUriService,
    private _kirjanpitoImageHandlerService: KirjanpitoImageHandlerService,
    private _dateService: DateService,
    private _tilikarttaJaettuService: TilikarttaJaettuService,
    private _asiakasJaettuService: AsiakasJaettuService,
  ) {
    for (const alv of [...MyyntiAlvt.kaikki, ...OstoAlvt.kaikki]) {
      this._alvTunnisteenMukaan.set(alv.tunniste, alv)
    }
    arvonMuutokset(this.naytaKasitellytKentta)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(nayta => this.vaihdaNaytaKasitellyt(nayta))
  }

  async ngOnInit() {
    this._valittuPalauteSubject
      .pipe(
        tap(() => this.naytettavatTositteetJaSivut.set(null)),
        switchMap(palaute => {
          if (!palaute) {
            return of<TositeJaNaytettavaSivut[]>([])
          }
          return this._asiakasService.annaAsiakasObservable(palaute.asiakasAvain).pipe(
            take(1),
            switchMap(asiakas => {
              if (!asiakas) {
                return of<TositeJaNaytettavaSivut[]>([])
              }
              const asiakkaanAvainTiedot: AsiakkaanAvainTiedot = { avain: asiakas.avain, asiakasId: asiakas.asiakasId }
              const uri = this._kirjanpitoUriService.annaKirjaukseenLiitettyjenTiedostojenUri(palaute.asiakasAvain, palaute.kirjausAvain)
              return this._firebase
                .firestoreDoc<KirjaukseenLiitettyjenTiedostojenSivut>(uri)
                .listen()
                .pipe(switchMap(sivut => this._kirjanpitoImageHandlerService.muunnaTositteiksiJaSivuiksi(asiakkaanAvainTiedot, sivut, null, null, false)))
            }),
          )
        }),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(tositteet => {
        this.naytettavatTositteetJaSivut.set(tositteet)
      })

    this._valittuPalauteSubject
      .pipe(
        tap(() => {
          this.naytettavaKirjaus.set(null)
          this.kirjausLataa.set(true)
        }),
        switchMap(palaute => {
          if (!palaute) {
            return of<{ kirjaus: Kirjaus | null; asiakasAvain: string } | null>(null)
          }
          const uri = this._kirjanpitoUriService.annaKirjauksenUri(palaute.asiakasAvain, palaute.kirjausAvain)
          return this._firebase
            .firestoreDoc<Kirjaus>(uri)
            .listen()
            .pipe(map(kirjaus => ({ kirjaus, asiakasAvain: palaute.asiakasAvain })))
        }),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(async tulos => {
        this.kirjausLataa.set(false)
        this.naytettavaKirjaus.set(tulos?.kirjaus ?? null)
        if (tulos?.kirjaus) {
          await this._lataaTilinimet(tulos.asiakasAvain, tulos.kirjaus.p)
        }
      })

    const kirjanpitajatPromise = firstValueFrom(this._kirjanpitajaService.kirjanpitajienNimitiedotMapObservable).then(kartta => {
      this._kirjanpitajatMap.set(kartta)
    })
    await Promise.all([this._lataaPalautteet(), kirjanpitajatPromise])
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  valitsePalaute(palaute: KirjausehdotusPalauteRivi) {
    this.valittuPalaute.set(palaute)
    this._valittuPalauteSubject.next(palaute)
  }

  formatPvm(p: number): string {
    return this._dateService.muotoileNumberPaiva(p, 'fi')
  }

  debetYhteensa(rivit: Kirjausrivi[]): number {
    return rivit.reduce((s, r) => s + (r.d ?? 0), 0)
  }

  kreditYhteensa(rivit: Kirjausrivi[]): number {
    return rivit.reduce((s, r) => s + (r.k ?? 0), 0)
  }

  onAlvRivi(rivi: Kirjausrivi): boolean {
    return !!rivi.t && _ALV_TILINUMEROT.has(rivi.t)
  }

  annaTilinNimi(tilinumero: string | null): string {
    if (!tilinumero || !this.valittuPalaute()) {
      return ''
    }
    return this._tiliNimetAsiakkaittain().get(this.valittuPalaute().asiakasAvain)?.get(tilinumero) ?? ''
  }

  annaAlvLyhenne(rivi: Kirjausrivi): string {
    return rivi.a ? (this._alvTunnisteenMukaan.get(rivi.a)?.lyhenne ?? '') : ''
  }

  private async _lataaTilinimet(asiakasAvain: string, pvm: number) {
    if (this._tiliNimetAsiakkaittain().has(asiakasAvain)) {
      return
    }
    this._tiliNimetAsiakkaittain.update(kokoelma => new Map(kokoelma).set(asiakasAvain, new Map()))
    try {
      const [asiakas, paatilikartta, asiakkaanTilikartta] = await Promise.all([
        firstValueFrom(this._asiakasService.annaAsiakasObservable(asiakasAvain).pipe(take(1))),
        this._annaPaatilikartta(),
        this._firebase.firestoreDoc<AsiakkaanTilikartta>(this._kirjanpitoUriService.annaAsiakkaanTilikartanUri(asiakasAvain)).get(),
      ])
      const tilikausi = asiakas ? this._asiakasJaettuService.annaTilikausiPaivalle(asiakas as Asiakas, this._dateService.numberToLocalDate(pvm)) : null
      const tilit = tilikausi && paatilikartta ? this._tilikarttaJaettuService.yhdistaAsiakkaantilikarttaPaatilikarttaan(paatilikartta, asiakkaanTilikartta, tilikausi) : []
      const nimet = new Map<string, string>()
      for (const tili of tilit) {
        nimet.set(tili.numero, this._tilikarttaJaettuService.annaKirjanpitotilinNimi(tili, 'fi'))
      }
      this._tiliNimetAsiakkaittain.update(kokoelma => new Map(kokoelma).set(asiakasAvain, nimet))
    } catch (err) {
      console.error('tilinimien lataus epäonnistui', err)
      this._tiliNimetAsiakkaittain.update(kokoelma => new Map([...kokoelma].filter(([avain]) => avain !== asiakasAvain)))
    }
  }

  private _annaPaatilikartta(): Promise<Paatilikartta> {
    if (!this._paatilikarttaPromise) {
      this._paatilikarttaPromise = this._firebase.firestoreDoc<Paatilikartta>('admin/paatilikartta').get()
    }
    return this._paatilikarttaPromise
  }

  annaEhdotetutTilit(palaute: KirjausehdotusPalauteRivi): string[] {
    const tilit = palaute.ehdotetutTilit ?? []
    return tilit.map((tili, i) => {
      const alv = palaute.ehdotetutAlv?.[i]
      return alv ? `${tili} (${alv})` : tili
    })
  }

  annaKirjanpitajanNimi(uid: string | null | undefined): string {
    if (!uid) {
      return '—'
    }
    const k = this._kirjanpitajatMap().get(uid)
    return k ? `${k.etunimi} ${k.sukunimi}` : uid
  }

  async vaihdaNaytaKasitellyt(nayta: boolean) {
    this.naytaKasitellyt = nayta
    await this._lataaPalautteet()
  }

  async merkitseKasitellyksi(palaute: KirjausehdotusPalauteRivi) {
    if (this.merkitaanKasitellyiksi().has(palaute.avain)) {
      return
    }
    this.merkitaanKasitellyiksi.update(joukko => new Set(joukko).add(palaute.avain))
    try {
      const vastaus = await callLlmKirjausehdotusPalaute(this._firebase, 'merkitseKasitellyksi', { avain: palaute.avain })
      if (vastaus.e) {
        this._snackbar.open('Merkintä epäonnistui. Yritä uudelleen.', 'OK', { duration: 5000 })
        return
      }
      await this._lataaPalautteet()
    } finally {
      this.merkitaanKasitellyiksi.update(joukko => new Set([...joukko].filter(avain => avain !== palaute.avain)))
    }
  }

  private async _lataaPalautteet() {
    this.lataa.set(true)
    try {
      const vastaus = await callLlmKirjausehdotusPalaute(this._firebase, 'haeKaikki', { sisaltaaKasitellyt: this.naytaKasitellyt })
      if (vastaus.e) {
        this._snackbar.open('Palautteiden haku epäonnistui.', 'OK', { duration: 5000 })
        this.palautteet.set([])
        return
      }
      this.palautteet.set(vastaus.palautteet ?? [])
      const valittu = this.valittuPalaute()
      if (valittu && !this.palautteet().some(p => p.avain === valittu.avain)) {
        this.valittuPalaute.set(null)
        this._valittuPalauteSubject.next(null)
      }
    } finally {
      this.lataa.set(false)
    }
  }
}
